import { Box, Flex, Text, keyframes } from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';

const versionBackgroundColor = colors.aurora[50];
const selectedBackgroundColor = colors.aurora[500];
const pulseAnimation = keyframes`
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.5;
  }
`;
export const datepickerStyles = {
  // style to use when a date has versions
  '.react-datepicker__day--highlight1': {
    backgroundColor: versionBackgroundColor,
    borderRadius: '50%',
    color: 'black',
  },

  // style to use when date is selected
  '.react-datepicker__day--highlight2': {
    backgroundColor: selectedBackgroundColor,
    borderRadius: '50%',
    color: 'white',
  },

  '.react-datepicker__day--loading': {
    backgroundColor: '#E2E8F0', // Subtle gray background color
    borderRadius: '50%',
    color: '#A0AEC0', // Lighter gray color for text
    opacity: 0.5, // Slightly transparent to indicate non-interactivity
    animation: `${pulseAnimation} 2s infinite`, // Pulse animation
    pointerEvents: 'none', // Make the dates non-selectable
  },
};

type HighlightDates = {
  [key: string]: Date[];
};

export const getHighlightedDates = ({
  selectedDate,
  versions,
  currentMonth,
}: {
  selectedDate: Util.FullDateStr | null;
  versions: Map<Util.FullDateStr, number> | null;
  currentMonth: Date;
}): HighlightDates[] => {
  if (!versions) {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    // get the current month's date range
    const startDate = new Date(year, month, 1);

    // month + 1 gives the next month, and 0 gives the last day of the current month
    const endDate = new Date(year, month + 1, 0);
    const dates: Date[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (
      let date = startDate;
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      dates.push(new Date(date));
    }

    // Apply the loading or non-selectable style to all dates in the current month
    return [
      {
        'react-datepicker__day--loading': dates,
      },
    ];
  }

  const dates = Array.from(versions.keys()).map((date) =>
    commonDate.fullDateStrToDate(date),
  );
  const customHighlightedDates: HighlightDates[] = [
    {
      'react-datepicker__day--highlight1': dates,
    },
  ];

  if (selectedDate) {
    customHighlightedDates.push({
      'react-datepicker__day--highlight2': [
        commonDate.fullDateStrToDate(selectedDate),
      ],
    });
  }

  return customHighlightedDates;
};

export const Legend = () => {
  return (
    <Box pb={4} display="flex" alignItems="center">
      <Flex alignItems="center" mr={6}>
        <Box
          bg={selectedBackgroundColor}
          w="16px"
          h="16px"
          mr={2}
          borderRadius="50%"
        />
        <Text fontSize="md">Selected Date</Text>
      </Flex>
      <Flex alignItems="center">
        <Box
          bg={versionBackgroundColor}
          w="16px"
          h="16px"
          mr={2}
          borderRadius="50%"
        />
        <Text fontSize="md">Data Tape Previously Uploaded</Text>
      </Flex>
    </Box>
  );
};
